import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardCard from "../components/Card";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import GSTIN from "../assets/images/GSTBANNER.jpg";
import INCOMETAX from "../assets/images/ITR.jpg";
import TDS from "../assets/images/TDSFILING.jpg";
import BannerImage from "../assets/images/sample2.jpg";
import { fetchData, postData } from "../api/apiService";
import { USERS } from "../api/endpointMap";
import { LoadingOutlined } from "@ant-design/icons";
import AppContext from "antd/es/app/context";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import ChartBg from "../assets/images/ChartBackground.svg";
import Logo from "../assets/images/NabsLogoOld.png";
import { useTutorial } from "../context/TutorialContext";
import { WelcomeModal } from "../components/Tutorial/WelcomeModal";
import { setUserVisiblePortals } from "../utils/portalAccess";
import UpgradeModal from "../components/UpgradeModal";
import ClientsPerStaffWidget from "../components/ClientsPerStaffWidget";
import NoticeAILogo from "../assets/images/LoadingLogo.png";
import { NoticeBanner } from "../components/NoticeBanner";
// Iframe Reuse Refactor — embed-mode flag + path-prefixing navigate.
// Outside an EmbedLayout tree, useEmbedMode() returns
// `{ isEmbed: false }` and useTenantNavigate is a no-op pass-through
// to react-router's useNavigate, so tenant users see zero behaviour
// change. See IFRAME_REUSE_IMPLEMENTATION_GUIDE.md §6 + §8.
import { useEmbedMode } from "../layouts/EmbedLayout";
import useTenantNavigate from "../hooks/useTenantNavigate";

const EnhancedDashboardCard = ({
  image,
  title,
  description,
  onClick,
  tutorialId,
  isEnabled = true,
  currentPlan = "Basic",
  handleRestrictedAccess,
}) => {
  const handleClick = () => {
    if (isEnabled) {
      onClick();
    } else {
      handleRestrictedAccess(title);
    }
  };

  return (
    <motion.div
      className={`relative group cursor-pointer ${
        !isEnabled ? "opacity-75" : ""
      }`}
      onClick={handleClick}
      whileHover={{ scale: isEnabled ? 1.03 : 1.01 }}
      whileTap={{ scale: isEnabled ? 0.97 : 0.99 }}
      data-tutorial={tutorialId}
      transition={{ type: "spring", stiffness: 150, damping: 20 }}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* Disabled card glow effect */}
      {!isEnabled && (
        <motion.div className="absolute -inset-1 bg-gradient-to-r from-gray-400 to-gray-500 rounded-xl blur opacity-0 group-hover:opacity-10 transition-all duration-300" />
      )}

      {/* Card Container */}
      <motion.div
        className={`relative rounded-xl shadow-lg overflow-hidden w-[350px] flex flex-col ${
          isEnabled ? "bg-white" : "bg-gray-50"
        }`}
        whileHover={{ scale: isEnabled ? 1.01 : 1.005 }}
        transition={{ type: "spring", stiffness: 120, damping: 15 }}
        style={{ minHeight: "340px" }}
      >
        <div
          className="relative overflow-hidden flex-shrink-0"
          style={{ height: "200px" }}
        >
          <motion.img
            src={image}
            alt={title}
            className={`w-full h-full object-cover transition duration-300 ${
              !isEnabled ? "blur-sm scale-105" : ""
            }`}
            whileHover={{ scale: isEnabled ? 1.08 : 1.02 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />

          {/* Overlay */}
          <motion.div
            className={`absolute inset-0 bg-gradient-to-t ${
              isEnabled
                ? "from-black/50 to-transparent opacity-0 group-hover:opacity-100"
                : "from-gray-900/70 to-gray-600/30 opacity-60"
            } transition-opacity duration-300`}
          />

          {/* Lock Icon for Disabled Cards */}
          {!isEnabled && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-white/95 backdrop-blur-sm rounded-full p-2 shadow-xl border border-gray-200">
                <svg
                  className="w-8 h-8 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              </div>
            </motion.div>
          )}

          {/* Arrow Icon for Enabled Cards */}
          {isEnabled && (
            <motion.div
              className="absolute top-4 right-4 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </motion.div>
          )}

          {/* Upgrade Badge for Disabled Cards */}

          {!isEnabled && (
            <motion.div
              className="absolute top-3 left-3 z-40 bg-black/60 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm backdrop-blur-sm border border-white/10"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              Upgrade Required
            </motion.div>
          )}
        </div>

        <div className="p-6 flex-1 flex flex-col justify-between">
          <div className="flex-1">
            <motion.h3
              className={`text-xl font-bold mb-2 ${
                isEnabled ? "text-blue-800" : "text-gray-500"
              }`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              {title}
            </motion.h3>

            {description && (
              <motion.p
                className={`text-sm ${
                  isEnabled ? "text-gray-600" : "text-gray-400"
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                {isEnabled
                  ? description
                  : `Unlock ${title.toLowerCase()} portal with a upgraded plan`}
              </motion.p>
            )}
          </div>

          {/* Action Button Area */}
          <div className="mt-4 space-y-2">
            <motion.div
              className={`h-1 rounded-full ${
                isEnabled
                  ? "bg-gradient-to-r from-blue-500 to-purple-600"
                  : "bg-gradient-to-r from-gray-300 to-gray-400"
              }`}
              initial={{ width: 0 }}
              whileInView={{ width: "100%" }}
              transition={{ duration: 0.8, delay: 0.4 }}
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Dashboard = () => {
  const { setPage } = useTutorial();
  const navigate = useNavigate();
  const tenantNavigate = useTenantNavigate();
  const { isEmbed } = useEmbedMode();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [restrictedPortal, setRestrictedPortal] = useState(null);

  const handleRestrictedAccess = (portalName) => {
    // In embed mode, plan upgrades are NoticeAI's billing relationship
    // with the subscriber — partner shouldn't surface that. Fall back
    // to a soft toast instead of opening the upgrade modal.
    if (isEmbed) {
      toast.warning(
        `Access to ${portalName} is not available. Contact your administrator.`,
      );
      return;
    }
    setRestrictedPortal(portalName);
    setShowUpgradeModal(true);
  };

  useEffect(() => {
    setPage("dashboard");
  }, [setPage]);

  const [loading, setLoading] = useState(true);
  const [userPortals, setUserPortals] = useState([]);
  const [currentPlan, setCurrentPlan] = useState("Basic");
  const { logout, checkReadOnlyMode, checkIsFirstPurchase } = useAuth();

  const portalMapping = {
    GST: "gstin",
    "Income Tax": "incometax",
    TDS: "tds",
  };

  const allCards = [
    {
      image: GSTIN,
      title: "GST",
      desc: "Manage your GST notices efficiently",
      link: "/gstin?type=gstin",
      tutorialId: "gst-card",
      page: "gstin",
      portalKey: "GST",
    },
    {
      image: INCOMETAX,
      title: "Income Tax",
      desc: "Handle income tax notices with ease",
      link: "/income-tax?type=incometax",
      page: "income-tax",
      tutorialId: "income-tax-card",
      portalKey: "Income Tax",
    },
    {
      image: TDS,
      title: "TDS/TCS",
      desc: "Streamline TDS/TCS notice management",
      link: "/tds?type=tds",
      page: "tds",
      tutorialId: "tds-card",
      portalKey: "TDS",
    },
  ];

  const getUserData = async () => {
    try {
      const response = await fetchData(USERS.USER_INFO);

      if (response?.is_first_purchase) {
        localStorage.setItem("isFirstPurchase", "1");
      }

      if (response?.organization?.enabled_portals) {
        const portalsObj = response.organization.enabled_portals;
        const portalMap = { income_tax: "Income Tax", gst: "GST", tds: "TDS" };
        const enabledPortals = Object.entries(portalsObj)
          .filter(([_, v]) => v)
          .map(([k]) => portalMap[k]);
        setUserPortals(enabledPortals);
        localStorage.setItem("enabledPortals", JSON.stringify(enabledPortals));
      }

      // Rehydrate the user-level visible_portals cache from /users/me so
      // that post-trial-start / post-purchase navigations see the new
      // org + user effective portal list without requiring a re-login.
      // Without this the Dashboard card filter sees the stale empty
      // array written by the original /auth/login response and hides
      // every portal card on first paint after the trial starts.
      if (Array.isArray(response?.visible_portals)) {
        setUserVisiblePortals(response.visible_portals);
      }

      if (response?.organization?.subscription_plan) {
        setCurrentPlan(response.organization.subscription_plan);
        localStorage.setItem("currentPlan", response.organization.subscription_plan);
      }

      if (response?.subscription_status) {
        const org = response.organization || {};
        const isExpired = response.subscription_status === "expired" || response.subscription_status === "trial_expired";
        // Parse "YYYY-MM-DD" as LOCAL midnight (avoids UTC-parse fractional
        // diff in IST). trial_end_date is the last active day (inclusive) → +1
        // in the diff so today==end shows "1 day left". Math.round handles
        // DST millisecond drift. Mirrors SubscriptionDropdown/index.jsx.
        const _parseLocal = (s) => {
          if (!s) return null;
          const [y, m, d] = s.split("-").map(Number);
          return new Date(y, m - 1, d);
        };
        const trialEnd = _parseLocal(org.trial_end_date);
        const subEnd = _parseLocal(org.subscription_end_date);
        const endDate = org.is_trial ? trialEnd : subEnd;
        const todayMid = new Date();
        todayMid.setHours(0, 0, 0, 0);
        const daysRemaining = endDate
          ? Math.max(0, Math.round((endDate - todayMid) / 86400000) + 1)
          : 0;

        const enabledPortalsList = [];
        const ep = org.enabled_portals || {};
        if (ep.income_tax) enabledPortalsList.push({ name: "Income Tax", enabled: true, category: org.is_trial ? "Trial" : org.subscription_category || "Active" });
        if (ep.gst) enabledPortalsList.push({ name: "GST", enabled: true, category: org.is_trial ? "Trial" : org.subscription_category || "Active" });
        if (ep.tds) enabledPortalsList.push({ name: "TDS", enabled: true, category: org.is_trial ? "Trial" : org.subscription_category || "Active" });

        const subscriptionData = {
          status: response.subscription_status,
          display_status: org.is_trial ? "Free Trial" : org.subscription_category || response.subscription_status,
          is_trial: org.is_trial,
          is_admin: response.role === "tenant_admin" || response.role === "super_admin",
          is_expired: isExpired,
          trial_days_remaining: org.is_trial ? daysRemaining : 0,
          days_remaining: daysRemaining,
          trial_end_date: org.trial_end_date,
          subscription_end_date: org.subscription_end_date,
          subscription_category: org.subscription_category,
          plan_name: org.subscription_plan,
          portals: enabledPortalsList,
        };
        localStorage.setItem("subscriptionStatus", JSON.stringify(subscriptionData));
      }

      if (
        response?.enabled === false &&
        response?.role !== "super_admin"
      ) {
        toast.error(
          "Your subscription has expired. Please contact NABS helpdesk for renewal!",
        );
        // In embed mode the iframe's session is owned by the parent —
        // local logout + login-redirect would break the session
        // contract. Skip and let the parent handle account state.
        if (!isEmbed) {
          logout();
          setTimeout(() => {
            navigate("/login");
          }, 500);
        }
      } else if (
        response?.enabled === true &&
        response?.read_only === true
      ) {
        localStorage.setItem("readOnlyMode", "true");
      } else if (response?.read_only === false) {
        // The flag MUST be cleared when the server says the account is not
        // read-only. Without this branch it was written once and never
        // removed except on logout, so an account that came out of read-only
        // — a renewed subscription, an extended trial, or an admin returning
        // from impersonating a read-only user — stayed greyed out for the
        // rest of the session with no way to recover but signing out.
        localStorage.removeItem("readOnlyMode");
      }
    } catch (err) {
      console.error(err);
      setUserPortals([]);
    } finally {
      setTimeout(() => {
        setLoading(false);
        checkReadOnlyMode();
        // checkIsFirstPurchase();
      }, 1500);
    }
  };

  useEffect(() => {
    getUserData();
  }, []);

  const isPortalEnabled = (portalKey) => {
    // Org-level (subscription) check only — drives the "Upgrade plan"
    // overlay. User-level check is separate (see hideForUser below)
    // because spec says user-disabled portals should be HIDDEN entirely,
    // not just rendered with an overlay.
    return userPortals.includes(portalKey);
  };

  const hideForUser = (portalKey) => {
    // Spec: "Agar kisi user ko sirf Income Tax portal ki access di hai
    // toh usko sirf Income Tax portal hi dikhega, other portals ke
    // cards BHI USKO NAHI DIKHNE CHAHIYE." User-disabled portals are
    // not just blocked — they should disappear from the page entirely.
    try {
      const {
        userHasPortalAccess,
      } = require("../utils/portalAccess");
      return !userHasPortalAccess(portalKey);
    } catch {
      return false;  // be permissive if helper unavailable
    }
  };

  const handleCardNavigation = (card) => {
    if (isPortalEnabled(card.portalKey)) {
      // tenantNavigate auto-prefixes with `/embed` when isEmbed=true,
      // so card links like `/gstin?type=gstin` resolve to
      // `/embed/gstin?type=gstin` inside the iframe and stay under
      // the embed wrapper. Outside the iframe (tenant app), this is
      // a no-op pass-through to react-router's `useNavigate`.
      tenantNavigate(card.link);
    } else {
      toast.warning(
        `Access to ${card.title} portal is not available in your current plan. Please upgrade to unlock this feature.`,
      );
    }
  };

  return (
    <>
      {loading ? (
        <div className="flex flex-col min-h-screen items-center justify-center bg-[#f0f5ff] relative">
          <div className="loader-wrapper">
            <img src={Logo} alt="Logo" className="logo" />
            <div className="border-loader"></div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col min-h-screen bg-[#f0f5ff]">
          {/* Top nav suppressed in embed: EmbedLayout already paints
              the partner-branded header. Showing NoticeAI's own Navbar
              on top of it would double the chrome and surface
              account-management actions (logout, profile) that the
              iframe shouldn't expose per spec §6. */}
          {!isEmbed && <Navbar />}

          <div className="w-full flex min-h-screen bg-linear-to-br from-[#f0f5ff] via-[#e6f2ff] to-[#dbeafe] relative overflow-hidden">
            <div className="relative flex items-center justify-center pt-16 flex-1 z-10">
              <div
                className="relative z-10 flex flex-col items-center text-center px-6 md:px-16"
                data-tutorial="main-dashboard"
              >
                {/* Hero block (NoticeAI logo + "Simplify Your Tax
                    Notices" tagline) is marketing chrome — keep for
                    the tenant app, drop in embed where the partner
                    owns the surrounding context. The portal cards
                    below are the actual functional surface. */}
                {!isEmbed && (
                  <motion.h1
                    className="text-4xl mt-8 md:mt-0 md:text-5xl font-bold text-white leading-tight p-4 rounded-lg"
                    initial={{ opacity: 0, y: -50, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.2,
                      type: "spring",
                      stiffness: 100,
                      damping: 15,
                    }}
                  >
                    <div className="flex items-center justify-center">
                      <img src={NoticeAILogo} width={250} alt="NoticeAI" />
                    </div>

                    <motion.span
                      className="text-blue-900 text-3xl drop-shadow-md"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.6, duration: 0.6 }}
                    >
                      Simplify Your Tax Notices
                    </motion.span>
                  </motion.h1>
                )}

                <motion.div
                  className="flex flex-col md:flex-row gap-8 mt-10"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.2 }}
                  variants={{
                    hidden: { opacity: 0, y: 100 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        staggerChildren: 0.2,
                        delayChildren: 0.1,
                      },
                    },
                  }}
                >
                  {allCards
                    .filter((card) => !hideForUser(card.portalKey))
                    .map((card, index) => {
                    const isEnabled = isPortalEnabled(card.portalKey);
                    return (
                      <motion.div
                        key={index}
                        variants={{
                          hidden: { opacity: 0, y: 100, rotateX: 45 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            rotateX: 0,
                            transition: {
                              type: "spring",
                              stiffness: 100,
                              damping: 15,
                              duration: 0.6,
                            },
                          },
                        }}
                      >
                        <EnhancedDashboardCard
                          image={card.image}
                          title={card.title}
                          description={card.desc}
                          onClick={() => handleCardNavigation(card)}
                          tutorialId={card.tutorialId}
                          isEnabled={isEnabled}
                          currentPlan={currentPlan}
                          handleRestrictedAccess={handleRestrictedAccess}
                        />
                      </motion.div>
                    );
                  })}
                </motion.div>
              </div>
            </div>
          </div>

          {/* Clients-per-Staff widget (USER_LEVEL_CLIENT_ASSIGNMENT.md
              §7.6). Self-suppresses for non-admin roles, single-user
              orgs, and the embed iframe (which has no /users access via
              partner-SSO JWT). */}
          {!isEmbed && (
            <div className="container mx-auto px-4">
              <ClientsPerStaffWidget />
            </div>
          )}

          {/* Footer (NoticeAI marketing/links) — tenant-only. */}
          {!isEmbed && <Footer />}
        </div>
      )}

      {/* UpgradeModal is the plan-upgrade billing surface; suppressed
          in embed per spec §6. The handleRestrictedAccess fallback in
          embed mode now toasts a "contact your administrator" message
          instead of opening this modal, so the modal is unreachable
          inside the iframe — keeping the JSX gated avoids even the
          wasted render. */}
      {!isEmbed && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          restrictedPortal={restrictedPortal}
        />
      )}

      {/* <WelcomeModal /> */}
    </>
  );
};
